import { DEFAULT_BASEMAP_ID, DEFAULT_VIEW_MODE } from './basemaps';
import type { MapViewMode } from './basemaps';
import type { FeatureOverlayState, PoiTypeState } from './mapLayers';
import { DEFAULT_FEATURE_OVERLAY_STATE, DEFAULT_POI_TYPE_STATE } from './mapLayers';

export const MAP_PREFERENCES_KEY = 'orbit.map.preferences.v1';

export interface MapViewport {
    longitude: number;
    latitude: number;
    zoom: number;
    bearing: number;
    pitch: number;
}

export interface MapPreferences {
    selectedBasemapId: string;
    viewMode: MapViewMode;
    featureOverlays: FeatureOverlayState;
    poiTypes: PoiTypeState;
    viewport?: MapViewport;
    /**
     * The client the remembered viewport was looking at. A viewport is a place in one client's
     * network, so it is restored only for that client; a map opened for another client flies
     * to that client's network instead. Absent on records saved before this was recorded,
     * which are restored as they always were.
     */
    viewportClientId?: string;
}

/** The viewport fields of either preferences record, for {@link restorableViewport}. */
export interface RememberedViewport {
    viewport?: MapViewport;
    viewportClientId?: string;
}

/**
 * The remembered viewport a map for `selectedClientId` may restore: the stored one when it
 * was saved for that client (or before the owner was recorded, or when no client is known
 * yet), otherwise none, so the map opens as on a cold start and flies to the client's
 * network. This is what keeps a switch made while no client map was mounted, such as
 * leaving Network Explorer onto a different client, from landing on the previous client's
 * location.
 */
export function restorableViewport(
    remembered: RememberedViewport,
    selectedClientId: string | null | undefined,
): MapViewport | undefined {
    if (remembered.viewport === undefined) return undefined;
    if (remembered.viewportClientId === undefined || !selectedClientId) return remembered.viewport;
    return remembered.viewportClientId === selectedClientId ? remembered.viewport : undefined;
}

/** Exported for the v2 preferences module, which stores the same owner alongside its viewport. */
export function validateViewportClientId(stored: unknown): string | undefined {
    return typeof stored === 'string' && stored.length > 0 ? stored : undefined;
}

function getDefaultPreferences(): MapPreferences {
    return {
        selectedBasemapId: DEFAULT_BASEMAP_ID,
        viewMode: DEFAULT_VIEW_MODE,
        featureOverlays: { ...DEFAULT_FEATURE_OVERLAY_STATE },
        poiTypes: { ...DEFAULT_POI_TYPE_STATE },
    };
}

/**
 * Merges stored overlay values onto the current defaults.
 * - Known keys present in stored data override the default.
 * - Keys absent from stored data (newly added layers) keep their configured default.
 * - Unknown stored keys (removed/renamed layers) are ignored.
 * - Non-boolean stored values are ignored.
 *
 * Exported for the v2 preferences module, which applies the same per-field tolerance.
 */
export function mergeOverlayState<K extends string>(defaults: Record<K, boolean>, stored: unknown): Record<K, boolean> {
    if (typeof stored !== 'object' || stored === null) return { ...defaults };
    const result = { ...defaults };
    for (const key of Object.keys(defaults) as K[]) {
        const value = (stored as Record<string, unknown>)[key];
        if (typeof value === 'boolean') {
            result[key] = value;
        }
    }
    return result;
}

/** Exported for the v2 preferences module, which applies the same viewport validation. */
export function validateViewport(stored: unknown): MapViewport | undefined {
    if (typeof stored !== 'object' || stored === null) return undefined;
    const v = stored as Record<string, unknown>;
    const { longitude, latitude, zoom, bearing, pitch } = v;

    if (typeof longitude !== 'number' || longitude < -180 || longitude > 180) return undefined;
    if (typeof latitude !== 'number' || latitude < -90 || latitude > 90) return undefined;
    if (typeof zoom !== 'number' || zoom < 0 || zoom > 24) return undefined;

    return {
        longitude,
        latitude,
        zoom,
        bearing: typeof bearing === 'number' && bearing >= -180 && bearing <= 180 ? bearing : 0,
        pitch: typeof pitch === 'number' && pitch >= 0 && pitch <= 85 ? pitch : 0,
    };
}

function parseRaw(raw: string): Record<string, unknown> | null {
    try {
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null) return null;
        if ((parsed as Record<string, unknown>)['version'] !== 1) return null;
        return parsed as Record<string, unknown>;
    } catch {
        return null;
    }
}

/**
 * Loads and validates map preferences from localStorage.
 * Falls back to configured defaults on any error or missing/invalid data.
 * Merges stored overlay state so newly added layer keys always use their configured default.
 */
export function loadMapPreferences(validBasemapIds: string[]): MapPreferences {
    try {
        const raw = localStorage.getItem(MAP_PREFERENCES_KEY);
        if (!raw) return getDefaultPreferences();

        const stored = parseRaw(raw);
        if (!stored) return getDefaultPreferences();

        const storedBasemapId = stored['selectedBasemapId'];
        const selectedBasemapId =
            typeof storedBasemapId === 'string' && validBasemapIds.includes(storedBasemapId)
                ? storedBasemapId
                : DEFAULT_BASEMAP_ID;

        const storedViewMode = stored['viewMode'];
        const viewMode: MapViewMode =
            storedViewMode === '2d' || storedViewMode === '3d' || storedViewMode === 'globe'
                ? storedViewMode
                : DEFAULT_VIEW_MODE;

        return {
            selectedBasemapId,
            viewMode,
            featureOverlays: mergeOverlayState(DEFAULT_FEATURE_OVERLAY_STATE, stored['featureOverlays']),
            poiTypes: mergeOverlayState(DEFAULT_POI_TYPE_STATE, stored['poiTypes']),
            viewport: validateViewport(stored['viewport']),
            viewportClientId: validateViewportClientId(stored['viewportClientId']),
        };
    } catch {
        return getDefaultPreferences();
    }
}

/** Saves map preferences to localStorage. Silently ignores write failures. */
export function saveMapPreferences(prefs: MapPreferences): void {
    try {
        localStorage.setItem(MAP_PREFERENCES_KEY, JSON.stringify({ version: 1, ...prefs }));
    } catch {
        // localStorage may be unavailable (private browsing, storage quota, etc.)
    }
}
